import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ScoreGauge } from "@/components/ScoreGauge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  Users, 
  Target, 
  Award, 
  PhoneCall,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Lightbulb,
  TrendingDown,
  Minus,
  MessageSquare,
  Share2,
  ThumbsUp,
  Calendar,
  Zap,
  Clock,
  RefreshCw,
  User,
  Sparkles
} from "lucide-react";
import { MetricWithTooltip } from "@/components/MetricWithTooltip";
import { QuickWinsCard } from "@/components/QuickWinsCard";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import lukaszProfile from "@/assets/lukasz-profile.jpg";
import { EmployeeAdvocacyMatrix } from "@/components/EmployeeAdvocacyMatrix";
import { NetworkReachVisualizer } from "@/components/NetworkReachVisualizer";
import { ViralContentShowcase } from "@/components/ViralContentShowcase";
import { ContentPerformanceChart } from "@/components/ContentPerformanceChart";
import { InfluenceLeaderboard } from "@/components/InfluenceLeaderboard";
import { EmotionReactionChart } from "@/components/EmotionReactionChart";
import { ContentCalendarHeatmap } from "@/components/ContentCalendarHeatmap";
import { TeamSkillsMatrix } from "@/components/TeamSkillsMatrix";
import { PostingPatternsTimeline } from "@/components/PostingPatternsTimeline";
import { EngagementQualityScore } from "@/components/EngagementQualityScore";
import { ContentTypePerformance } from "@/components/ContentTypePerformance";
import { AIRecommendationsCard } from "@/components/AIRecommendationsCard";
import { AIInsightCard } from "@/components/AIInsightCard";
import { AuditResultsSummary } from "@/components/AuditResultsSummary";
import { LinkedInAdsCard } from "@/components/LinkedInAdsCard";
import { cn } from "@/lib/utils";

interface AuditReport {
  generatedAt: string;
  company: {
    domain: string;
    name: string;
    metricsUnavailable?: boolean;
    metricsUnavailableReason?: string;
    linkedInType?: 'company' | 'personal-profile';
    owner?: {
      name: string;
      position?: string;
      headline?: string;
      profileUrl: string;
      location?: string;
      note?: string;
    };
    metrics?: {
      followerCount: number;
      employeeCount: number;
      postingFrequency: string;
      avgEngagement: number;
      recentPosts?: any[];
      formatDiversity?: {
        text: number;
        image: number;
        video: number;
        article: number;
        poll: number;
      };
      tagline?: string;
      description?: string;
      logo?: string;
      industry?: string;
      location?: string;
      founded?: number;
      specialties?: string[];
      companyType?: string;
      website?: string;
    };
    adsData?: {
      hasActiveAds: boolean;
      adCount: number;
      campaigns?: any[];
      dateRange?: {
        earliest: string;
        latest: string;
      };
    };
    employeeData?: {
      totalAnalyzed: number;
      avgProfileCompleteness: number;
      avgActivityScore?: number;
      activityBreakdown?: {
        activePublishers: number;
        occasionalPublishers: number;
        inactive: number;
      };
      contentThemes?: {
        companyNews: number;
        thoughtLeadership: number;
        productShowcase: number;
        recruitment: number;
        other: number;
      };
      topPerformers?: any[];
      advocacyDistribution?: {
        advocates: number;
        contributors: number;
        passive: number;
        dormant: number;
      };
      networkReach?: {
        directConnections: number;
        estimatedSecondDegree: number;
        potentialImpressions: number;
      };
      contentInsights?: {
        bestPerformingPosts?: Array<{
          author: string;
          authorPosition?: string;
          text: string;
          engagement: number;
          reactions: any;
          comments: number;
          shares: number;
          engagementMultiplier: number;
          publishedAt: string;
        }>;
        themePerformance?: Array<{
          theme: string;
          postCount: number;
          avgEngagement: number;
          trend: 'up' | 'down' | 'stable';
        }>;
      };
      viralContent?: Array<{
        postId: string;
        author: string;
        authorPosition?: string;
        text: string;
        engagement: number;
        engagementMultiplier: number;
        publishedAt: string;
      }>;
      // NEW: Phase 1-3 Enhanced Data
      skillsAnalysis?: any;
      educationAnalysis?: any;
      postingPatterns?: any;
      emotionBreakdown?: any;
      engagementQuality?: any;
      influenceLeaderboard?: any[];
    };
  };
  competitors: Array<{
    domain: string;
    name: string;
    linkedInType?: 'company' | 'personal-profile';
    metrics?: {
      followerCount: number;
      avgEngagement: number;
      formatDiversity?: any;
      employeeCount?: number;
      tagline?: string;
      description?: string;
      location?: string;
      industry?: string;
    };
    adsData?: {
      hasActiveAds: boolean;
      adCount: number;
    };
    employeeData?: {
      avgActivityScore?: number;
      activityBreakdown?: any;
      networkReach?: {
        directConnections: number;
        estimatedSecondDegree: number;
        potentialImpressions: number;
      };
    };
  }>;
  benchmarks: {
    performanceScore: number;
    ranking: number;
  };
  insights: {
    executiveSummary: string;
    performanceScore: number;
    subScores?: {
      companyPageHealth: number;
      organicActivity: number;
      employeeAdvocacy: number;
      paidPresence: number;
      siteToLinkedIn: number;
    };
    keyFindings: string[];
    recommendations: Array<{
      title: string;
      priority: number | string;
      description: string;
      expectedImpact: string;
      timeline?: string;
      phase?: string;
    }>;
    actionPlan?: {
      days30: string[];
      days60: string[];
      days90: string[];
    };
    consistencyScore?: {
      score: number;
      analysis: string;
    };
  };
  meta: {
    analyzedCompanies: number;
    lastRetryAt?: string;
    confidenceLevels?: {
      linkedInCompanyData?: string;
      employeeData?: string;
      competitorData?: string;
      websiteScan?: string;
      overall?: string;
    };
  };
}

const SocialSellingReport = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<AuditReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/raport-socialselling');
      return;
    }

    const fetchReport = async () => {
      try {
        // Disable cache by adding timestamp to force fresh data
        const timestamp = Date.now();
        const { data, error } = await supabase
          .from('social_selling_submissions')
          .select('report_data, status')
          .eq('id', id)
          .gte('created_at', '2000-01-01') // Dummy filter to bypass cache
          .maybeSingle()
          .throwOnError();

        if (error) throw error;

        if (!data) {
          setError('Nie znaleziono raportu');
          return;
        }

        if (data.status !== 'completed' || !data.report_data) {
          navigate(`/raport-socialselling/status/${id}`);
          return;
        }

        setReport(data.report_data as unknown as AuditReport);
      } catch (err: any) {
        console.error('Error fetching report:', err);
        setError(err.message || 'Wystąpił błąd');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id, navigate]);

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      await supabase.functions.invoke('retry-audit', {
        body: { submissionId: id }
      });
      navigate(`/raport-socialselling/status/${id}`);
    } catch (e) {
      console.error('Retry audit error:', e);
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Ładowanie raportu...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-destructive">Błąd</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error || 'Nie znaleziono raportu'}</p>
            <Button onClick={() => navigate('/raport-socialselling')} className="w-full">
              Wróć do formularza
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, competitors, benchmarks, insights, meta } = report;
  const score = benchmarks.performanceScore / 10; // Convert to 0-10 scale
  
  const hasLowConfidence = meta && (meta as any).confidenceLevels && 
    Object.values((meta as any).confidenceLevels).some((c: any) => c === 'low' || c === 'very-low');
  
  // Check if most metrics are zero (indicates data collection failure)
  const hasZeroMetrics = 
    (company.metrics?.followerCount ?? 0) === 0 &&
    (company.metrics?.employeeCount ?? 0) === 0 &&
    (company.metrics?.recentPosts?.length ?? 0) === 0;

  return (
    <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-8 md:py-16">
        <div className="container px-4">
            <div className="max-w-5xl mx-auto space-y-8">

              <div className="flex items-center justify-end">
                <Button variant="outline" onClick={handleRegenerate} disabled={regenerating}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                  {regenerating ? 'Przegenerowuję...' : 'Przegeneruj raport'}
                </Button>
              </div>

            {/* Warning Banner for Zero Metrics (High Priority) */}
            {hasZeroMetrics && hasLowConfidence && (
              <Alert variant="default" className="border-red-400 bg-red-50 dark:bg-red-900/10">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <AlertTitle className="text-red-800 dark:text-red-200">
                  ⚠️ Brak danych z LinkedIn
                </AlertTitle>
                <AlertDescription className="text-red-700 dark:text-red-300 space-y-2">
                  <p>Nie udało się pobrać kompletnych danych z LinkedIn. Może to wynikać z:</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Problemów z formatowaniem URL LinkedIn (znaki specjalne, kodowanie)</li>
                    <li>Ustawień prywatności na profilu LinkedIn</li>
                    <li>Ograniczeń API zewnętrznych serwisów</li>
                  </ul>
                  <p className="font-semibold mt-2">
                    💡 Spróbuj ponownie za 24 godziny lub skontaktuj się z nami po pomoc.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {/* Warning Banner for Low Confidence Data (Lower Priority) */}
            {hasLowConfidence && !hasZeroMetrics && (
              <Alert variant="default" className="border-yellow-300 bg-yellow-50 dark:bg-yellow-900/10">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <AlertTitle className="text-yellow-800 dark:text-yellow-200">
                  Część danych jest szacunkowa
                </AlertTitle>
                <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                  Niektóre API nie zwróciły pełnych danych. Wyniki mogą być częściowo oparte na oszacowaniach.
                  {(meta as any).confidenceLevels?.linkedInCompanyData === 'low' && ' LinkedIn Company data jest niepewne.'}
                  {(meta as any).confidenceLevels?.employeeData === 'low' && ' Dane pracowników są szacunkowe.'}
                  {(meta as any).confidenceLevels?.competitorData === 'low' && ' Analiza konkurencji jest ograniczona.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Warning Banner for Metrics Unavailable */}
            {company.metricsUnavailable && (
              <Alert variant="default" className="border-orange-300 bg-orange-50 dark:bg-orange-900/10">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                <AlertTitle className="text-orange-800 dark:text-orange-200">
                  Metryki LinkedIn niedostępne
                </AlertTitle>
                <AlertDescription className="text-orange-700 dark:text-orange-300">
                  {company.metricsUnavailableReason || 'Strona firmowa na LinkedIn została znaleziona, ale metryki są niedostępne. Spróbuj ponownie później.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Hero Section */}
            <Card className="border-none shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background p-8 md:p-12">
                <div className="text-center mb-8">
                  <Badge className="mb-4" variant="secondary">
                    <Award className="h-3 w-3 mr-1" />
                    Raport Social Selling LinkedIn
                  </Badge>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h1 className="text-3xl md:text-4xl font-bold">
                      {company.name}
                    </h1>
                    {company.linkedInType === 'personal-profile' && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700">
                              <User className="h-3 w-3 mr-1" />
                              Konto osobiste
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>To jest profil osobisty, a nie strona firmowa</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                   <p className="text-muted-foreground">
                     Utworzono: {new Date(report.generatedAt).toLocaleString('pl-PL')}
                     {report.meta?.lastRetryAt && (
                       <>
                         <br />
                         Ostatnie odświeżenie: {new Date(report.meta.lastRetryAt).toLocaleString('pl-PL')}
                       </>
                     )}
                   </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <ScoreGauge score={score} />
                  </div>
                  <div className="md:col-span-2 flex flex-col justify-center space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-background/50 rounded-lg">
                      <TrendingUp className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Pozycja w rankingu</p>
                        <p className="text-2xl font-bold">
                          #{benchmarks.ranking} z {meta.analyzedCompanies}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-background/50 rounded-lg">
                        <Users className="h-5 w-5 text-primary mb-1" />
                        <p className="text-xs text-muted-foreground">Obserwujący</p>
                        <p className="font-semibold">
                          {company.metricsUnavailable ? 'N/A' : (company.metrics?.followerCount || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-background/50 rounded-lg">
                        <BarChart3 className="h-5 w-5 text-primary mb-1" />
                        <p className="text-xs text-muted-foreground">Zaangażowanie</p>
                        <p className="font-semibold">
                          {company.metricsUnavailable ? 'N/A' : (company.metrics?.avgEngagement || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-background/50 rounded-lg">
                        <Users className="h-5 w-5 text-green-500 mb-1" />
                        <p className="text-xs text-muted-foreground">Aktywni pracownicy</p>
                        <p className="font-semibold">{company.employeeData?.totalAnalyzed || 0}</p>
                      </div>
                      <div className="p-3 bg-background/50 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 mb-1" />
                        <p className="text-xs text-muted-foreground">Profile pracowników</p>
                        <p className="font-semibold">{company.employeeData?.avgProfileCompleteness || 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Podsumowanie</CardTitle>
              </CardHeader>
              <CardContent>
                <AuditResultsSummary reportData={report} expanded />
              </CardContent>
            </Card>
            
            {/* Sub-Scores Breakdown */}
            {insights.subScores && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Szczegółowa Punktacja
                  </CardTitle>
                  <CardDescription>
                    Analiza 5 kluczowych obszarów (wagi: 20%, 25%, 20%, 25%, 10%)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <MetricWithTooltip
                      label="Company Page Health"
                      value={insights.subScores.companyPageHealth}
                      icon={Award}
                      tooltip="Stan profilu firmowego na LinkedIn: kompletność informacji, aktualność danych, obecność logo, opisu i tagline. Pełny profil buduje zaufanie i zwiększa widoczność."
                    >
                      <Badge variant="outline" className="text-xs mr-2">20%</Badge>
                      <Progress value={insights.subScores.companyPageHealth} className="h-3 mt-2" />
                    </MetricWithTooltip>
                    
                    <MetricWithTooltip
                      label="Organic Activity"
                      value={insights.subScores.organicActivity}
                      icon={TrendingUp}
                      tooltip="Regularność i jakość niereklamowych publikacji. Aktywna strona firmowa generuje naturalny zasięg, buduje autorytet i angażuje obserwujących bez kosztów reklamowych."
                    >
                      <Badge variant="outline" className="text-xs mr-2">25%</Badge>
                      <Progress value={insights.subScores.organicActivity} className="h-3 mt-2" />
                    </MetricWithTooltip>
                    
                    <MetricWithTooltip
                      label="Employee Advocacy"
                      value={insights.subScores.employeeAdvocacy}
                      icon={Users}
                      tooltip="Zaangażowanie pracowników w promowanie firmy na LinkedIn. Pracownicy mają średnio 10x więcej połączeń niż strona firmowa - ich posty docierają do szerszej grupy potencjalnych klientów."
                    >
                      <Badge variant="outline" className="text-xs mr-2">20%</Badge>
                      <Progress value={insights.subScores.employeeAdvocacy} className="h-3 mt-2" />
                    </MetricWithTooltip>
                    
                    <MetricWithTooltip
                      label="Paid Presence"
                      value={insights.subScores.paidPresence}
                      icon={Zap}
                      tooltip="Aktywność w płatnych kampaniach reklamowych na LinkedIn. Reklamy zwiększają zasięg, ale bez organicznej obecności mogą być mniej skuteczne."
                    >
                      <Badge variant="outline" className="text-xs mr-2">25%</Badge>
                      <Progress value={insights.subScores.paidPresence} className="h-3 mt-2" />
                    </MetricWithTooltip>
                    
                    <MetricWithTooltip
                      label="Site ↔ LinkedIn"
                      value={insights.subScores.siteToLinkedIn}
                      icon={Target}
                      tooltip="Integracja strony www z LinkedIn: Insight Tag (tracking), Open Graph (podgląd linków). Prawidłowa integracja pozwala mierzyć ROI i poprawia wygląd udostępnianych treści."
                    >
                      <Badge variant="outline" className="text-xs mr-2">10%</Badge>
                      <Progress value={insights.subScores.siteToLinkedIn} className="h-3 mt-2" />
                    </MetricWithTooltip>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Owner Profile (Personal LinkedIn) */}
            {company.linkedInType === 'personal-profile' && company.owner && (
              <Card className="border-amber-500/20 bg-amber-500/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-amber-600" />
                    Profil Właściciela
                  </CardTitle>
                  <CardDescription>
                    {company.owner.note || 'Dane z profilu osobowego LinkedIn'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-8 w-8 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{company.owner.name}</h3>
                        {company.owner.position && (
                          <p className="text-sm text-muted-foreground">{company.owner.position}</p>
                        )}
                        {company.owner.headline && (
                          <p className="text-sm text-muted-foreground mt-1">{company.owner.headline}</p>
                        )}
                        {company.owner.location && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            {company.owner.location}
                          </p>
                        )}
                        <a 
                          href={company.owner.profileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline mt-2 inline-block"
                        >
                          Zobacz profil LinkedIn →
                        </a>
                      </div>
                    </div>
                    
                    {company.metrics && (
                      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t">
                        <div className="text-center p-3 bg-primary/10 rounded-lg">
                          <Users className="h-4 w-4 mx-auto mb-1 text-primary" />
                          <p className="text-xl font-bold">{company.metrics.followerCount?.toLocaleString() || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground">Kontaktów</p>
                        </div>
                        <div className="text-center p-3 bg-primary/10 rounded-lg">
                          <MessageSquare className="h-4 w-4 mx-auto mb-1 text-primary" />
                          <p className="text-xl font-bold">{company.metrics.recentPosts?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Ostatnich postów</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* LinkedIn Company Profile Details */}
            {!company.metricsUnavailable && company.metrics && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Profil LinkedIn - Szczegóły
                  </CardTitle>
                  <CardDescription>
                    Dane pobrane z CoreSignal API
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Company Info Column */}
                    <div className="space-y-4">
                      {company.metrics.logo && (
                        <div className="flex items-center gap-4 mb-4">
                          <img 
                            src={company.metrics.logo} 
                            alt={`${company.name} logo`}
                            className="w-16 h-16 rounded-lg object-contain bg-secondary p-2"
                          />
                          <div>
                            <h3 className="font-bold text-lg">{company.name}</h3>
                            {company.metrics.tagline && (
                              <p className="text-sm text-muted-foreground">{company.metrics.tagline}</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {company.metrics.description && (
                        <div>
                          <h4 className="font-semibold mb-2 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            Opis
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {company.metrics.description.length > 300 
                              ? `${company.metrics.description.substring(0, 300)}...` 
                              : company.metrics.description}
                          </p>
                        </div>
                      )}

                      {company.metrics.specialties && company.metrics.specialties.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Specjalizacje</h4>
                          <div className="flex flex-wrap gap-2">
                            {company.metrics.specialties.map((specialty, idx) => (
                              <Badge key={idx} variant="secondary">{specialty}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Metrics Column */}
                    <div className="space-y-3">
                      {company.metrics.industry && (
                        <div className="flex items-start gap-3 p-3 bg-secondary/20 rounded-lg">
                          <BarChart3 className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Branża</p>
                            <p className="font-medium">{company.metrics.industry}</p>
                          </div>
                        </div>
                      )}

                      {company.metrics.location && (
                        <div className="flex items-start gap-3 p-3 bg-secondary/20 rounded-lg">
                          <Target className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Lokalizacja</p>
                            <p className="font-medium">{company.metrics.location}</p>
                          </div>
                        </div>
                      )}

                      {company.metrics.founded && (
                        <div className="flex items-start gap-3 p-3 bg-secondary/20 rounded-lg">
                          <Calendar className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Rok założenia</p>
                            <p className="font-medium">{company.metrics.founded}</p>
                          </div>
                        </div>
                      )}

                      {company.metrics.companyType && (
                        <div className="flex items-start gap-3 p-3 bg-secondary/20 rounded-lg">
                          <Award className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Typ firmy</p>
                            <p className="font-medium">{company.metrics.companyType}</p>
                          </div>
                        </div>
                      )}

                      {company.metrics.website && (
                        <div className="flex items-start gap-3 p-3 bg-secondary/20 rounded-lg">
                          <Target className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Strona www</p>
                            <a 
                              href={company.metrics.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="font-medium text-primary hover:underline"
                            >
                              {company.metrics.website}
                            </a>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3 pt-3">
                        <div className="text-center p-4 bg-primary/10 rounded-lg">
                          <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
                          <p className="text-2xl font-bold">{company.metrics.followerCount.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Obserwujących</p>
                        </div>
                        <div className="text-center p-4 bg-primary/10 rounded-lg">
                          <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
                          <p className="text-2xl font-bold">{company.metrics.employeeCount.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Pracowników</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* LinkedIn Ads Section */}
            {company.adsData && (
              <LinkedInAdsCard 
                adsData={company.adsData}
                companyName={company.name}
              />
            )}

            {/* Executive Summary - AI Enhanced */}
            <AIInsightCard
              title="Podsumowanie Wykonawcze"
              content={insights.executiveSummary}
              icon={<Target className="h-5 w-5 text-primary" />}
              variant="highlight"
            />

            {/* Key Findings - AI Enhanced */}
            <AIInsightCard
              title="Kluczowe Obserwacje"
              content={insights.keyFindings}
              icon={<Lightbulb className="h-5 w-5 text-primary" />}
              variant="default"
            />

            {/* Content Format Diversity */}
            {company.metrics?.formatDiversity && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Różnorodność Formatów Treści
                  </CardTitle>
                  <CardDescription>
                    Analiza {company.metrics.recentPosts?.length || 0} ostatnich postów
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { name: 'Tekst', count: company.metrics.formatDiversity.text, icon: MessageSquare },
                      { name: 'Obraz', count: company.metrics.formatDiversity.image, icon: ThumbsUp },
                      { name: 'Wideo', count: company.metrics.formatDiversity.video, icon: Share2 },
                      { name: 'Artykuł', count: company.metrics.formatDiversity.article, icon: Target },
                      { name: 'Ankieta', count: company.metrics.formatDiversity.poll, icon: BarChart3 }
                    ].map((format, idx) => {
                      const Icon = format.icon;
                      const total = Object.values(company.metrics?.formatDiversity || {}).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? Math.round((format.count / total) * 100) : 0;
                      return (
                        <div key={idx} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{format.name}</span>
                            </div>
                            <span className="text-sm font-bold">{format.count} ({percentage}%)</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employee Engagement */}
            {company.employeeData && (
              <>
                {/* Employee Advocacy Matrix */}
                {company.employeeData.advocacyDistribution && (
                  <EmployeeAdvocacyMatrix
                    distribution={company.employeeData.advocacyDistribution}
                    totalEmployees={company.employeeData.totalAnalyzed}
                  />
                )}

                {/* Network Reach Visualizer */}
                {company.employeeData.networkReach && (
                  <NetworkReachVisualizer
                    networkReach={company.employeeData.networkReach}
                    companyName={company.name}
                    competitorAverage={
                      competitors.length > 0 
                        ? Math.round(
                            competitors.reduce((sum, c) => 
                              sum + (c.employeeData?.networkReach?.potentialImpressions || 0), 0
                            ) / competitors.length
                          )
                        : undefined
                    }
                  />
                )}

                {/* Viral Content Showcase */}
                {company.employeeData.viralContent && company.employeeData.viralContent.length > 0 && (
                  <ViralContentShowcase
                    viralPosts={company.employeeData.viralContent}
                    avgEngagement={
                      company.employeeData.contentInsights?.bestPerformingPosts?.[0]?.engagement || 0
                    }
                  />
                )}

                {/* Content Performance Chart */}
                {company.employeeData.contentInsights?.themePerformance && (
                  <ContentPerformanceChart
                    themePerformance={company.employeeData.contentInsights.themePerformance}
                  />
                )}

                {/* NEW: Phase 1-3 Enhanced Components */}
                
                {/* Influence Leaderboard */}
                {(company.employeeData as any)?.influenceLeaderboard && (
                  <InfluenceLeaderboard 
                    topEmployees={(company.employeeData as any).influenceLeaderboard}
                  />
                )}

                {/* Team Skills Matrix */}
                {((company.employeeData as any)?.skillsAnalysis || (company.employeeData as any)?.educationAnalysis) && (
                  <TeamSkillsMatrix
                    skillsAnalysis={(company.employeeData as any).skillsAnalysis}
                    educationAnalysis={(company.employeeData as any).educationAnalysis}
                  />
                )}

                {/* Posting Patterns Timeline */}
                {(company.employeeData as any)?.postingPatterns?.monthlyTrend && (
                  <PostingPatternsTimeline
                    timeline={(company.employeeData as any).postingPatterns.monthlyTrend}
                  />
                )}

                {/* Content Calendar Heatmap */}
                {(company.employeeData as any)?.postingPatterns && (
                  <ContentCalendarHeatmap
                    postingPatterns={(company.employeeData as any).postingPatterns}
                  />
                )}

                {/* Emotion Reaction Chart */}
                {(company.employeeData as any)?.emotionBreakdown && (
                  <EmotionReactionChart
                    reactions={(company.employeeData as any).emotionBreakdown}
                  />
                )}

                {/* Engagement Quality Score */}
                {(company.employeeData as any)?.engagementQuality && (
                  <EngagementQualityScore
                    engagementQuality={(company.employeeData as any).engagementQuality}
                  />
                )}

                {/* Original Employee Engagement Card */}
                <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Aktywność Pracowników
                  </CardTitle>
                  <CardDescription>
                    Analiza {company.employeeData.totalAnalyzed} profili pracowników na LinkedIn
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="overview">Przegląd</TabsTrigger>
                      <TabsTrigger value="activity">Aktywność</TabsTrigger>
                      <TabsTrigger value="themes">Tematyka</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="overview" className="space-y-4 mt-4">
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="text-center p-6 bg-secondary/20 rounded-lg">
                          <p className="text-3xl font-bold text-primary mb-2">
                            {company.employeeData.totalAnalyzed}
                          </p>
                          <p className="text-sm text-muted-foreground">Przeanalizowanych profili</p>
                        </div>
                        <div className="text-center p-6 bg-secondary/20 rounded-lg">
                          <p className="text-3xl font-bold text-blue-500 mb-2">
                            {company.employeeData.avgProfileCompleteness}%
                          </p>
                          <p className="text-sm text-muted-foreground">Kompletność profili</p>
                        </div>
                        <div className="text-center p-6 bg-secondary/20 rounded-lg">
                          <p className="text-3xl font-bold text-green-500 mb-2">
                            {company.employeeData.avgActivityScore}
                          </p>
                          <p className="text-sm text-muted-foreground">Wynik aktywności</p>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="activity" className="space-y-4 mt-4">
                      {company.employeeData.activityBreakdown && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg">
                            <TrendingUp className="h-8 w-8 text-green-500" />
                            <div className="flex-1">
                              <p className="font-semibold">Aktywni wydawcy (≥2 posty/mies.)</p>
                              <p className="text-2xl font-bold">{company.employeeData.activityBreakdown.activePublishers}</p>
                            </div>
                            <Progress 
                              value={(company.employeeData.activityBreakdown.activePublishers / company.employeeData.totalAnalyzed) * 100} 
                              className="w-24 h-2"
                            />
                          </div>
                          <div className="flex items-center gap-3 p-4 bg-yellow-500/10 rounded-lg">
                            <Minus className="h-8 w-8 text-yellow-500" />
                            <div className="flex-1">
                              <p className="font-semibold">Okazjonalni (1 post/mies.)</p>
                              <p className="text-2xl font-bold">{company.employeeData.activityBreakdown.occasionalPublishers}</p>
                            </div>
                            <Progress 
                              value={(company.employeeData.activityBreakdown.occasionalPublishers / company.employeeData.totalAnalyzed) * 100} 
                              className="w-24 h-2"
                            />
                          </div>
                          <div className="flex items-center gap-3 p-4 bg-red-500/10 rounded-lg">
                            <TrendingDown className="h-8 w-8 text-red-500" />
                            <div className="flex-1">
                              <p className="font-semibold">Nieaktywni</p>
                              <p className="text-2xl font-bold">{company.employeeData.activityBreakdown.inactive}</p>
                            </div>
                            <Progress 
                              value={(company.employeeData.activityBreakdown.inactive / company.employeeData.totalAnalyzed) * 100} 
                              className="w-24 h-2"
                            />
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="themes" className="space-y-4 mt-4">
                      {company.employeeData.contentThemes && (
                        <div className="space-y-3">
                          {[
                            { name: 'Company News', value: company.employeeData.contentThemes.companyNews },
                            { name: 'Thought Leadership', value: company.employeeData.contentThemes.thoughtLeadership },
                            { name: 'Product Showcase', value: company.employeeData.contentThemes.productShowcase },
                            { name: 'Recruitment', value: company.employeeData.contentThemes.recruitment },
                            { name: 'Inne', value: company.employeeData.contentThemes.other }
                          ].map((theme, idx) => (
                            <div key={idx} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{theme.name}</span>
                                <span className="text-sm font-bold">{theme.value}%</span>
                              </div>
                              <Progress value={theme.value} className="h-2" />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Real Posts from PhantomBuster */}
                      {(company.employeeData as any)?.totalPosts && (company.employeeData as any).totalPosts > 0 && (
                        <div className="mt-6 pt-6 border-t">
                          <Alert className="mb-4">
                            <CheckCircle2 className="h-4 w-4" />
                            <AlertTitle>Prawdziwe dane z postów pracowników</AlertTitle>
                            <AlertDescription>
                              Zebrano {(company.employeeData as any).totalPosts} postów od {(company.employeeData as any).topPublishers?.length || 0} najbardziej aktywnych pracowników
                            </AlertDescription>
                          </Alert>
                          
                          {/* Top Publishers */}
                          {(company.employeeData as any).topPublishers && (company.employeeData as any).topPublishers.length > 0 && (
                            <div className="space-y-3">
                              <h4 className="font-semibold">Najaktywniejsze osoby:</h4>
                              {(company.employeeData as any).topPublishers.map((pub: any, idx: number) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                                  <div>
                                    <p className="font-medium">{pub.name}</p>
                                    <p className="text-xs text-muted-foreground">{pub.postCount} postów</p>
                                  </div>
                                  <Badge>{pub.avgEngagement} śr. zaangażowanie</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </>
            )}

            {/* Competitive Comparison */}
            {competitors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Porównanie z Konkurencją
                  </CardTitle>
                  <CardDescription>
                    Twoja pozycja względem {competitors.length} firm konkurencyjnych
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="overview">Porównanie</TabsTrigger>
                      <TabsTrigger value="details">Szczegóły firm</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-6 mt-4">
                      {/* Comparative Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-2 text-sm font-medium">Pozycja</th>
                              <th className="text-left py-3 px-2 text-sm font-medium">Firma</th>
                              <th className="text-right py-3 px-2 text-sm font-medium">Obserwujący</th>
                              <th className="text-right py-3 px-2 text-sm font-medium">Zaangażowanie</th>
                              <th className="text-right py-3 px-2 text-sm font-medium">Reklamy</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Your Company */}
                            <tr className="bg-primary/5 border-2 border-primary/20">
                              <td className="py-3 px-2">
                                <Badge variant="default">#{benchmarks.ranking}</Badge>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <div>
                                    <p className="font-semibold">{company.name}</p>
                                    <p className="text-xs text-muted-foreground">Twoja firma</p>
                                  </div>
                                  {company.linkedInType === 'personal-profile' && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700 text-xs">
                                            <User className="h-3 w-3 mr-1" />
                                            Konto
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>To jest profil osobisty, a nie strona firmowa</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-2 text-right font-semibold">
                                {company.metricsUnavailable ? 'N/A' : (company.metrics?.followerCount?.toLocaleString() || 'N/A')}
                              </td>
                              <td className="py-3 px-2 text-right font-semibold">
                                {company.metricsUnavailable ? 'N/A' : (company.metrics?.avgEngagement || 0)}
                              </td>
                              <td className="py-3 px-2 text-right">
                                <Badge variant={company.adsData?.hasActiveAds ? "default" : "secondary"}>
                                  {company.adsData?.hasActiveAds ? `${company.adsData.adCount} akt.` : 'Brak'}
                                </Badge>
                              </td>
                            </tr>
                            
                            {/* Competitors */}
                            {competitors.map((comp, idx) => (
                              <tr key={idx} className="border-b hover:bg-secondary/10">
                                <td className="py-3 px-2">
                                  <Badge variant="outline">#{idx + (benchmarks.ranking > idx + 1 ? idx + 1 : idx + 2)}</Badge>
                                </td>
                                <td className="py-3 px-2">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{comp.name}</p>
                                    {comp.linkedInType === 'personal-profile' && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700 text-xs">
                                              <User className="h-3 w-3 mr-1" />
                                              Konto
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>To jest profil osobisty, a nie strona firmowa</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-2 text-right">
                                  {comp.metrics?.followerCount?.toLocaleString() || 'N/A'}
                                </td>
                                <td className="py-3 px-2 text-right">
                                  {comp.metrics?.avgEngagement || 'N/A'}
                                </td>
                                <td className="py-3 px-2 text-right">
                                  <Badge variant={comp.adsData?.hasActiveAds ? "default" : "secondary"}>
                                    {comp.adsData?.hasActiveAds ? `${comp.adsData.adCount} akt.` : 'Brak'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </TabsContent>

                    <TabsContent value="details" className="space-y-4 mt-4">
                      {competitors.map((comp, idx) => (
                        <Card key={idx} className="border-2">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <CardTitle className="text-lg">{comp.name}</CardTitle>
                                  {comp.linkedInType === 'personal-profile' && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700">
                                            <User className="h-3 w-3 mr-1" />
                                            Konto osobiste
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>To jest profil osobisty, a nie strona firmowa</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                {comp.metrics?.tagline && (
                                  <p className="text-sm text-muted-foreground mt-1">{comp.metrics.tagline}</p>
                                )}
                              </div>
                              <Badge variant="outline">Konkurent {idx + 1}</Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid md:grid-cols-2 gap-4">
                              {/* Left column - Description */}
                              <div className="space-y-3">
                                {comp.metrics?.description && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-muted-foreground mb-1">Opis</h5>
                                    <p className="text-sm">
                                      {comp.metrics.description.length > 200 
                                        ? `${comp.metrics.description.substring(0, 200)}...` 
                                        : comp.metrics.description}
                                    </p>
                                  </div>
                                )}

                                <div className="grid grid-cols-2 gap-2">
                                  {comp.metrics?.industry && (
                                    <div className="p-2 bg-secondary/20 rounded">
                                      <p className="text-xs text-muted-foreground">Branża</p>
                                      <p className="text-sm font-medium">{comp.metrics.industry}</p>
                                    </div>
                                  )}
                                  {comp.metrics?.location && (
                                    <div className="p-2 bg-secondary/20 rounded">
                                      <p className="text-xs text-muted-foreground">Lokalizacja</p>
                                      <p className="text-sm font-medium">{comp.metrics.location}</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Right column - Metrics */}
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="text-center p-3 bg-primary/10 rounded-lg">
                                    <Users className="h-4 w-4 mx-auto mb-1 text-primary" />
                                    <p className="text-lg font-bold">{comp.metrics?.followerCount?.toLocaleString() || 'N/A'}</p>
                                    <p className="text-xs text-muted-foreground">Obserwujący</p>
                                  </div>
                                  <div className="text-center p-3 bg-primary/10 rounded-lg">
                                    <BarChart3 className="h-4 w-4 mx-auto mb-1 text-primary" />
                                    <p className="text-lg font-bold">{comp.metrics?.avgEngagement || 'N/A'}</p>
                                    <p className="text-xs text-muted-foreground">Zaangażowanie</p>
                                  </div>
                                  {comp.metrics?.employeeCount && (
                                    <div className="text-center p-3 bg-secondary/20 rounded-lg">
                                      <Users className="h-4 w-4 mx-auto mb-1" />
                                      <p className="text-lg font-bold">{comp.metrics.employeeCount.toLocaleString()}</p>
                                      <p className="text-xs text-muted-foreground">Pracowników</p>
                                    </div>
                                  )}
                                  {comp.adsData && (
                                    <div className="text-center p-3 bg-secondary/20 rounded-lg">
                                      <Zap className="h-4 w-4 mx-auto mb-1" />
                                      <p className="text-lg font-bold">{comp.adsData.hasActiveAds ? comp.adsData.adCount : 0}</p>
                                      <p className="text-xs text-muted-foreground">Reklam</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* LinkedIn Ads Detail */}
                            {comp.adsData && comp.adsData.hasActiveAds && (
                              <div className="col-span-2 mt-4">
                                <LinkedInAdsCard
                                  adsData={comp.adsData}
                                  companyName={comp.name}
                                  isCompetitor={true}
                                />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* Quick Wins Card */}
            {insights.recommendations && insights.recommendations.length > 0 && (
              <QuickWinsCard recommendations={insights.recommendations} />
            )}

            {/* Action Plan - Timeline Style */}
            {insights.actionPlan && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Roadmap Wdrożenia
                  </CardTitle>
                  <CardDescription>
                    Szczegółowy harmonogram działań na najbliższe 90 dni
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative space-y-8 pl-8">
                    {/* Vertical timeline line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-success via-primary to-secondary opacity-30" />
                    
                    {/* Phase 1: 0-30 days */}
                    <div className="relative">
                      <div className="absolute -left-8 w-12 h-12 rounded-full bg-success flex items-center justify-center text-success-foreground font-bold shadow-lg z-10">
                        30d
                      </div>
                      <Card className="border-2 border-success/30 bg-success/5">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Zap className="h-5 w-5 text-success" />
                            Fundament (0-30 dni)
                          </CardTitle>
                          <CardDescription>Szybkie wygrane - niski wysiłek, wysoki impact</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {insights.actionPlan.days30.map((action, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 hover:bg-success/10 rounded transition-colors">
                              <CheckCircle2 className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                              <p className="text-sm">{action}</p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Phase 2: 30-60 days */}
                    <div className="relative">
                      <div className="absolute -left-8 w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold shadow-lg z-10">
                        60d
                      </div>
                      <Card className="border-2 border-primary/30 bg-primary/5">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            Budowanie Momentum (30-60 dni)
                          </CardTitle>
                          <CardDescription>Rozwijanie strategii i procesów</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {insights.actionPlan.days60.map((action, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 hover:bg-primary/10 rounded transition-colors">
                              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <p className="text-sm">{action}</p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Phase 3: 60-90 days */}
                    <div className="relative">
                      <div className="absolute -left-8 w-12 h-12 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold shadow-lg z-10">
                        90d
                      </div>
                      <Card className="border-2 border-secondary bg-secondary/20">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Award className="h-5 w-5 text-secondary-foreground" />
                            Optymalizacja (60-90 dni)
                          </CardTitle>
                          <CardDescription>Dojrzewanie i skalowanie działań</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {insights.actionPlan.days90.map((action, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 hover:bg-secondary/30 rounded transition-colors">
                              <CheckCircle2 className="h-4 w-4 text-secondary-foreground mt-0.5 flex-shrink-0" />
                              <p className="text-sm">{action}</p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Consistency Score */}
            {insights.consistencyScore && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Spójność Komunikacji
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-primary">{insights.consistencyScore.score}</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                      <Progress value={insights.consistencyScore.score} className="flex-1 h-4" />
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {insights.consistencyScore.analysis}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations - AI Enhanced */}
            <Card className="border-primary/20 shadow-glow overflow-hidden animate-fade-in">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 pointer-events-none" />
              
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 backdrop-blur-sm">
                    <Lightbulb className="h-6 w-6 text-primary animate-pulse-slow" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                      Rekomendacje AI
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Konkretne kroki do poprawy wyników social selling
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="relative">
                <div className="space-y-6">
                  {insights.recommendations
                    .sort((a, b) => {
                      const priorityA = typeof a.priority === 'number' ? a.priority : parseInt(String(a.priority)) || 999;
                      const priorityB = typeof b.priority === 'number' ? b.priority : parseInt(String(b.priority)) || 999;
                      return priorityA - priorityB;
                    })
                    .map((rec, index) => {
                      const isHighPriority = rec.priority === 1 || rec.priority === 'high';
                      const priorityGlow = isHighPriority 
                        ? 'shadow-[0_0_20px_rgba(239,68,68,0.3)]' 
                        : 'shadow-[0_0_15px_rgba(59,130,246,0.2)]';
                      
                      return (
                        <div
                          key={index}
                          className={cn(
                            "group relative p-6 rounded-xl border backdrop-blur-sm",
                            "bg-gradient-to-br from-card/50 to-card/30",
                            "hover:shadow-xl hover:scale-[1.02] transition-all duration-300",
                            "animate-fade-in",
                            priorityGlow
                          )}
                          style={{ animationDelay: `${index * 150}ms` }}
                        >
                          {/* Animated gradient background */}
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          
                          <div className="relative">
                            <div className="flex items-start justify-between gap-4 mb-4">
                              <div className="flex items-start gap-3">
                                <div className={cn(
                                  "p-2 rounded-lg transition-all duration-300",
                                  isHighPriority ? "bg-destructive/10 group-hover:animate-pulse" : "bg-primary/10"
                                )}>
                                  {isHighPriority ? (
                                    <AlertCircle className="h-5 w-5 text-destructive" />
                                  ) : (
                                    <CheckCircle2 className="h-5 w-5 text-primary" />
                                  )}
                                </div>
                                <h3 className="font-bold text-lg flex-1">{rec.title}</h3>
                              </div>
                              <Badge 
                                className={cn(
                                  "uppercase tracking-wide animate-scale-in",
                                  isHighPriority 
                                    ? "bg-gradient-to-r from-destructive to-destructive/80 text-destructive-foreground shadow-lg"
                                    : "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md"
                                )}
                                style={{ animationDelay: `${index * 150 + 100}ms` }}
                              >
                                P{rec.priority}
                              </Badge>
                            </div>

                            <p className="text-muted-foreground leading-relaxed mb-4">
                              {rec.description}
                            </p>

                            {/* Impact section with gradient */}
                            <div className="p-4 rounded-lg bg-gradient-to-r from-success/10 to-success/5 border border-success/30 backdrop-blur-sm">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="text-success h-5 w-5 animate-float" />
                                <p className="text-sm font-semibold text-success-foreground">
                                  Expected Impact: <span className="font-bold">{rec.expectedImpact}</span>
                                </p>
                              </div>
                            </div>

                            {rec.timeline && (
                              <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                <span>Timeline: {rec.timeline}</span>
                              </div>
                            )}

                            {rec.phase && (
                              <div className="mt-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {rec.phase}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>

                {/* AI Badge footer with shimmer effect */}
                <div className="mt-8 p-5 rounded-xl bg-gradient-to-r from-muted/80 to-muted/50 backdrop-blur-sm border border-border/50 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent animate-shimmer" 
                       style={{ backgroundSize: "200% 100%" }} />
                  <div className="relative flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 animate-pulse-slow" />
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Te rekomendacje zostały wygenerowane przez AI na podstawie dogłębnej analizy Twoich danych LinkedIn 
                      i porównania z best practices w branży Social Selling.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="border-none shadow-2xl overflow-hidden">
              <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-background p-8 md:p-12">
                <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5"></div>
                <div className="relative grid md:grid-cols-2 gap-8 items-center">
                  <div className="order-2 md:order-1">
                    <h2 className="text-2xl md:text-3xl font-bold mb-4">
                      Gotowy na wdrożenie?
                    </h2>
                    <p className="text-muted-foreground mb-6 text-lg">
                      Skonsultuj wyniki z naszym ekspertem i otrzymaj spersonalizowany plan działania
                    </p>
                    <div className="space-y-3">
                      <Button size="lg" className="w-full md:w-auto">
                        <PhoneCall className="mr-2 h-5 w-5" />
                        Zadzwoń teraz: +48 513 485 062
                      </Button>
                      <Button size="lg" variant="outline" className="w-full md:w-auto ml-0 md:ml-3">
                        <Download className="mr-2 h-5 w-5" />
                        Pobierz raport PDF
                      </Button>
                    </div>
                  </div>
                  <div className="order-1 md:order-2">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/10 rounded-full blur-3xl"></div>
                      <img 
                        src={lukaszProfile}
                        alt="Łukasz - Ekspert Social Selling" 
                        className="relative rounded-2xl shadow-2xl w-full max-w-sm mx-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export default SocialSellingReport;
